import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { login, getToken, readSignOutReason, clearSignOutReason } from "@/lib/auth";
import { Banner, BrandMark, Button, PasswordInput, Spinner } from "@/components/ui";
import { useLanguage } from "@/i18n";

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Why the previous session was refused, if it was (see lib/auth.ts).
  // Read in the initializer, cleared in an effect: StrictMode runs the
  // initializer twice, so clearing there would lose it on the second run.
  const [signedOutReason] = useState(readSignOutReason);
  const { t } = useLanguage();

  useEffect(() => {
    clearSignOutReason();
  }, []);

  useEffect(() => {
    if (getToken()) navigate("/", { replace: true });
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate("/", { replace: true });
    } catch (err) {
      const text = err instanceof Error ? err.message : t.login.signInFailed;
      setError(
        /failed to fetch|networkerror/i.test(text)
          ? t.login.cantReachServer
          : text
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sheet-bg flex h-full w-full overflow-y-auto">
      {/* The sheet: survey-ink navy with a graticule and a title block. Hidden
          on narrow screens, where the form alone is the page. */}
      <aside
        className="relative hidden w-[44%] max-w-[640px] flex-none flex-col justify-between overflow-hidden bg-chrome p-10 text-chrome-text md:flex"
        style={{
          backgroundImage:
            "linear-gradient(rgba(205,217,229,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(205,217,229,0.06) 1px, transparent 1px), radial-gradient(120% 80% at 100% 0%, rgba(0,150,199,0.28), transparent 55%)",
          backgroundSize: "48px 48px, 48px 48px, 100% 100%",
        }}
      >
        <div className="reveal flex items-center gap-3">
          <BrandMark className="h-10 w-10" />
          <div>
            <p className="font-display text-lg font-bold uppercase leading-tight tracking-[0.1em] text-white">Sthapatya</p>
            <p className="text-2xs uppercase tracking-[0.14em] text-chrome-muted">{t.login.brandSubtitle}</p>
          </div>
        </div>

        <div className="reveal" style={{ "--i": 1 } as React.CSSProperties}>
          <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-signal">GeoSurvey</p>
          <p className="mt-3 max-w-md font-display text-5xl font-bold uppercase leading-[0.95] tracking-[0.01em] text-white">
            Every property,
            <br />
            on the map.
          </p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-chrome-text/80">{t.login.subtitle}</p>
        </div>

        {/* Title block, as in the corner of a drawing. */}
        <dl
          className="reveal grid max-w-sm grid-cols-2 border border-chrome-border text-2xs uppercase tracking-[0.12em]"
          style={{ "--i": 2 } as React.CSSProperties}
        >
          <div className="border-b border-r border-chrome-border p-2.5">
            <dt className="text-chrome-muted">Sheet</dt>
            <dd className="mt-0.5 font-display text-sm font-semibold text-white">Administration</dd>
          </div>
          <div className="border-b border-chrome-border p-2.5">
            <dt className="text-chrome-muted">Datum</dt>
            <dd className="mt-0.5 font-display text-sm font-semibold text-white">WGS 84</dd>
          </div>
          <div className="col-span-2 p-2.5">
            <dt className="text-chrome-muted">Department</dt>
            <dd className="mt-0.5 font-display text-sm font-semibold text-white">Property tax · Field survey</dd>
          </div>
        </dl>
      </aside>

      <main className="flex min-w-0 flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-[380px]">
          <div className="reveal mb-6 flex items-center gap-2.5 md:hidden">
            <BrandMark className="h-9 w-9" />
            <div>
              <p className="font-display text-base font-bold uppercase leading-tight tracking-[0.08em] text-ink">Sthapatya</p>
              <p className="text-2xs leading-tight text-ink-muted">{t.login.brandSubtitle}</p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="reveal relative overflow-hidden rounded-md border border-line bg-surface p-6 shadow-lift"
            style={{ "--i": 1 } as React.CSSProperties}
          >
            <span className="absolute inset-x-0 top-0 h-[3px] bg-signal" aria-hidden="true" />
            <h1 className="font-display text-2xl font-bold uppercase tracking-[0.04em] text-ink">{t.login.title}</h1>
            <p className="mt-1 text-xs text-ink-muted">{t.login.subtitle}</p>

            <label className="mt-5 block">
              <span className="mb-1.5 block font-display text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-muted">
                {t.login.username}
              </span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                className="h-10 w-full rounded border border-line bg-surface-sunken px-3 text-sm text-ink transition focus:border-accent-solid focus:bg-surface-raised focus:outline-none"
              />
            </label>

            <label className="mt-4 block">
              <span className="mb-1.5 block font-display text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-muted">
                {t.login.password}
              </span>
              <PasswordInput
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                className="h-10 w-full rounded border border-line bg-surface-sunken pl-3 pr-9 text-sm text-ink transition focus:border-accent-solid focus:bg-surface-raised focus:outline-none disabled:opacity-50"
              />
            </label>

            {signedOutReason && !error && (
              <div className="mt-4">
                <Banner tone="warning">
                  {t.login.signedOutReason} {signedOutReason}
                </Banner>
              </div>
            )}

            {error && (
              <div className="mt-4">
                <Banner tone="danger">{error}</Banner>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="mt-5 h-10 w-full font-display text-sm uppercase tracking-[0.1em]"
              disabled={submitting || !username || !password}
            >
              {submitting ? <Spinner className="h-3.5 w-3.5" /> : null}
              {submitting ? t.login.signingIn : t.login.signIn}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
